/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { JsonLd } from 'json-ld-types';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import {
	CONFLUENCE,
	CORE,
	PRODUCT_DISCOVERY,
	SERVICE_DESK,
	SOFTWARE,
} from '@jira/platform__application-editions/src/application-key.tsx';
import type { DocNode as ADF } from '@atlaskit/adf-schema/doc';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { CardContext } from '@atlaskit/link-provider/context';
import { AnyAri } from '@atlassian/ari/any-ari';
// eslint-disable-next-line jira/restricted/@jira+platform__services-providers-project-context
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';

export const SUPPORTED_PRODUCTS: {
	confluence: string;
} = {
	[CONFLUENCE]: 'Confluence',
};

export type Product = keyof typeof SUPPORTED_PRODUCTS;

export const getProductFromProjectType = (
	projectType: ProjectType | undefined,
): 'jira-software' | 'jira-servicedesk' | 'jira-core' | 'jira-product-discovery' | null => {
	switch (projectType) {
		case 'software':
			return SOFTWARE;
		case 'service_desk':
			return SERVICE_DESK;
		case 'business':
			return CORE;
		case 'product_discovery':
			return PRODUCT_DISCOVERY;
		default:
			return null;
	}
};

const getProductFromSubproduct = (subproduct?: string) => {
	switch (subproduct) {
		case 'software':
		case 'jira_software':
			return SOFTWARE;
		case 'servicedesk':
		case 'jira_service_management':
			return SERVICE_DESK;
		case 'core':
		case 'jira_work_management':
			return CORE;
		case 'polaris':
		case 'jira_product_discovery':
			return PRODUCT_DISCOVERY;
		default:
			return null;
	}
};

export const isConfluenceLink = (url?: string, cloudName?: string): url is string => {
	if (!url || !cloudName) return false;
	const regex = new RegExp(`^https?://${cloudName}.atlassian.net/wiki(?!/external)`);
	return regex.test(url);
};

export type LinkData = {
	name?: string;
	tenantId: string;
	product: Product;
};

export const getAllLinks = (doc: ADF): Set<string> => {
	const links = new Set<string>();

	const handleLink = (link?: string) => {
		link && links.add(link);
	};

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSmartLink = (node: any) => handleLink(node.attrs?.url);

	traverse(doc, {
		text: (node) => {
			if ((node.marks || []).some((mark) => mark.type === 'link')) {
				handleLink(node.text);
			}
		},
		inlineCard: handleSmartLink,
		blockCard: handleSmartLink,
		embedCard: handleSmartLink,
	});

	return links;
};

const getSmartLinkData = (cloudId: string, details?: JsonLd.Response) => {
	if (!details) return;

	// oxlint-disable-next-line typescript/consistent-type-assertions
	const { name } = details.data as JsonLd.Data.BaseData;
	const { tenantId, subproduct } = details.meta;
	let { product } = details.meta;

	if (tenantId !== cloudId) return;

	product = product === 'jira' ? getProductFromSubproduct(subproduct) : product;
	if (!Object.keys(SUPPORTED_PRODUCTS).includes(product)) return;

	return { name, tenantId, product };
};

export const getAllProductLinks = (
	doc: ADF,
	store: CardContext['store'],
	cloudId: string,
	cloudName?: string,
): Record<string, LinkData> => {
	const links: Record<string, LinkData> = {};

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSmartLink = (node: any) => {
		// Check URL is on the ADF node
		const { url } = node?.attrs;
		if (!url) return;

		// Check URL is in the Smart Links Store
		const details = store.getState()[url]?.details;
		const data = getSmartLinkData(cloudId, details);

		if (data) links[url] = data;
	};

	const handleRawConfluenceLink = (url?: string) => {
		if (isConfluenceLink(url, cloudName)) {
			links[url] = {
				tenantId: cloudId,
				product: CONFLUENCE,
			};
		}
	};

	traverse(doc, {
		text: (node) => {
			if ((node.marks || []).some((mark) => mark.type === 'link')) {
				handleRawConfluenceLink(node.text);
			}
		},
		inlineCard: handleSmartLink,
		blockCard: handleSmartLink,
		embedCard: handleSmartLink,
	});

	return links;
};

let id = 0;
autoBeforeEachTest(() => {
	id = 0;
});
export const generateId = (): string => {
	id += 1;
	return String(id);
};

export const clampString = (string: string, maxLength: number): string => {
	if (string.length > maxLength) {
		return string.slice(0, maxLength).concat('...');
	}
	return string;
};

export const getProductDisplayName = (product: Product): string => SUPPORTED_PRODUCTS[product];

export const getSiteAri = (tenantId: string, product: Product): string =>
	AnyAri.create({
		resourceOwner: product,
		resourceType: 'site',
		resourceId: tenantId,
	}).toString();

export const useCurrentProduct = ():
	| 'jira-software'
	| 'jira-servicedesk'
	| 'jira-core'
	| 'jira-product-discovery' => {
	const { data } = useProjectContext();
	return getProductFromProjectType(data?.projectType) || SOFTWARE;
};
